import PropTypes from 'prop-types';
import React from 'react';

import { AjaxListingContext, InfinitePagination } from '$partials/base/ajax-listing.jsx';
import { GridBlock } from '$partials/grid/layout/index.js';
import { Meta, Footer, FilterButton } from '$partials/global/index.js';
import { NavigationPageWrapper, NavigationButton } from '$partials/navigation/index.js';
import { useAjaxListing } from '$hooks/ajax-listing';
import GridModuleDispatcher from '$partials/grid/dispatcher.jsx';
import { useMultiSelectFilter } from '$hooks/filters';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const ProjectListingPage = (props) => {
    const page = props.data.page;

    const gridSize = {
        height: 1,
        left: 0,
        right: 0,
        top: 0,
        bottom: 0
    };

    const tagFilter = useMultiSelectFilter(page?.items?.filters?.tags);

    const listing = useAjaxListing({
        infinite: true,
        items: page?.items?.edges,
        path: `/api/v1/listing/${encodeURIComponent(props.data.uri)}`,
        query: {
            'tags': tagFilter.value
        }
    });

    page?.allTags?.edges?.sort((a, b) => b?.node?.projects?.total - a?.node?.projects?.total);

    return (
        <>
            <Meta page={page} />

            <NavigationPageWrapper {...props.data?.navigation}>
                <main id='main-content'>
                    <div className='p-1'>
                        <div className='rounded mb-[1px] layout-container pt-20 sm:pt-40 pb-28 sm:pb-40 lg:pb-55 bg-foreground'>
                            <header className='px-layout-item type-style-2'>
                                <div className='rounded flex flex-col gap-32 sm:gap-60 md:gap-80'>
                                    <a
                                        className='flex flex-col'
                                        href={'/'}>
                                        <span>Bureau for</span>
                                        <span>Visual Affairs</span>
                                    </a>

                                    <h1 className='type-style-hero'>
                                        {page?.displayTitle}
                                    </h1>

                                    <NavigationButton />
                                </div>

                                {page?.allTags?.edges.length > 0 && (
                                    <div className='mt-15 sm:mt-22 md:mt-30 lg:mt-27 -mx-layout-item width-full overflow-x-auto flex gap-10 hide-scrollbar snap-x'>
                                        {page?.allTags?.edges.map(({ node: item }) => {
                                            return (
                                                <div
                                                    className='snap-start flex first:pl-layout-item last:pr-layout-item'
                                                    key={`filterKey-${item.id}`}>
                                                    <FilterButton
                                                        filterColour={item.categoryFilterColour}
                                                        handleFilter={() => {
                                                            tagFilter.toggleValue(item.slug);
                                                        }}
                                                        isSelected={tagFilter.value.includes(item.slug)}
                                                        title={item.title}
                                                        total={item.projects?.total}>
                                                    </FilterButton>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </header>
                        </div>

                        {/* Outer grid */}
                        <div className='w-full overflow-hidden rounded'>
                            <div className='grid grid-cols-2 sm:grid-cols-5 md:grid-cols-7 lg:grid-cols-9 bg-background -mr-1'>

                                <AjaxListingContext.Provider value={listing}>
                                    {listing?.items?.length > 0 ? listing?.items?.map((item, i) => {
                                        const node = item?.node;
                                        return (
                                            <span
                                                className='project-listing__wrapper contents'
                                                key={`fragment-${i}`}>
                                                <GridBlock
                                                    {...gridSize}
                                                    className='project-listing__title-block'
                                                    width={node?.listingTitleWidth}>
                                                    <a href={node?.relativeUrl}>
                                                        <div className={`h-full w-full content-center ${listing?.loading ? 'opacity-25' : ''}`}>
                                                            <h3 className='type-style-3 my-auto sm:text-center px-layout-item sm:px-10'>
                                                                {node?.title}
                                                            </h3>
                                                        </div>
                                                    </a>
                                                </GridBlock>

                                                {node?.listingImageBlocks?.map((imageBlock, i) => {
                                                    return (
                                                        <React.Fragment key={`imageBlock-${imageBlock?.id}`}>
                                                            <GridModuleDispatcher
                                                                className={`project-listing__image-block ${listing?.loading ? 'opacity-25' : ''} ${i > 0 ? 'hidden sm:block' : ''}`}
                                                                {...imageBlock}
                                                                {...gridSize}
                                                                assetDefinesMobileHeight={false}
                                                                relativeUrl={node?.relativeUrl} />
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </span>
                                        );
                                    }) : (
                                        <div
                                            className='relative hidden sm:block aspect-1/1'>
                                            <div className='absolute top-0 bottom-0 left-0 right-0 mr-1 mb-1 bg-foreground rounded overflow-hidden flex items-center'>
                                                <p className='m-auto type-style-4 text-center my-auto'>
                                                    No results
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    <InfinitePagination>
                                        <div
                                            className='relative hidden sm:block aspect-1/1'>
                                            <div className='absolute top-0 bottom-0 left-0 right-0 mr-1 mb-1 bg-foreground rounded overflow-hidden flex items-center'>
                                                <p className='m-auto type-style-4 text-center my-auto'>
                                                    Loading...
                                                </p>
                                            </div>
                                        </div>
                                    </InfinitePagination>
                                </AjaxListingContext.Provider>

                            </div>
                        </div>

                        <Footer
                            mailchimpId={props?.data?.mailchimpId?.mailchimpId}
                            {...props?.data?.footer} />
                    </div>
                </main>
            </NavigationPageWrapper>
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const propTypes = {
    data: PropTypes.shape({
        page: PropTypes.object,
        footer: PropTypes.object,
        navigation: PropTypes.object,
        uri: PropTypes.string,
        mailchimpId: PropTypes.object,
    }),
    domain: PropTypes.string,
};


ProjectListingPage.propTypes = propTypes;

export default ProjectListingPage;
